import {
  ColorSwatchIcon,
  KeyIcon,
  LightningBoltIcon,
  LockClosedIcon,
  ServerIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TerminalIcon,
  TranslateIcon,
  UserGroupIcon,
  UserIcon
} from '@heroicons/react/solid';

export const generalSuggestions: Array<{
  title: string;
  description: string;
  url: string;
  icon: (props: React.ComponentProps<'svg'>) => JSX.Element;
}> = [
  {
    title: 'Quickstarts & Tutorials',
    description:
      'Explore our end-to-end tutorials and getting started guides for different application stacks using Clerk. ',
    url: '/docs/quickstarts/overview',
    icon: LightningBoltIcon
  },
  {
    title: 'UI Components',
    description: `Clerk's pre-built UI components give you a beautiful, fully-functional user
    management experience in minutes.`,
    url: '/docs/components/overview',
    icon: SparklesIcon
  },
  {
    title: 'API Reference',
    description:
      'Dig into our API reference documentation and SDKs. We have everything you need to get started setting up authentication with Clerk.',
    url: '/docs/references/overview',
    icon: TerminalIcon
  },
  {
    title: 'Security',
    description: `Account security is the top concern of every feature we build. This documentation lists some of the many protections included with Clerk.`,
    url: '/docs/security/overview',
    icon: LockClosedIcon
  }
];

export const frontendSuggestions = (
  isLightMode: boolean
): Array<{
  title: string;
  description: string;
  url: string;
  logoIcon: string;
  logoSize: 'sm' | 'md';
}> => [
  {
    title: 'Next.js',
    description:
      'Easily add secure, beautiful, and fast authentication to Next.js with Clerk.',
    url: '/docs/quickstarts/nextjs',
    logoIcon: isLightMode
      ? '/images/logos/nextjs.svg'
      : '/images/logos/nextjs-white.svg',
    logoSize: 'md'
  },
  {
    title: 'React',
    description:
      'Get started installing and initializing Clerk in a new Create React App.',
    url: '/docs/quickstarts/react',
    logoIcon: '/images/logos/react.svg',
    logoSize: 'sm'
  },
  {
    title: 'Remix',
    description:
      'Easily add secure, edge- and SSR-friendly authentication to Remix with Clerk.',
    url: '/docs/quickstarts/remix',
    logoIcon: isLightMode
      ? '/images/logos/remix.svg'
      : '/images/logos/remix-white.svg',
    logoSize: 'sm'
  },
  {
    title: 'Gatsby',
    description:
      'Learn about installing and initializing Clerk in a new Gatsby application.',
    url: '/docs/quickstarts/gatsby',
    logoIcon: '/images/logos/gatsby.svg',
    logoSize: 'sm'
  },
  {
    title: 'RedwoodJS',
    description:
      'Grow your RedwoodJS application with Clerk user management and authentication.',
    url: '/docs/quickstarts/redwood',
    logoIcon: '/images/logos/redwood.svg',
    logoSize: 'sm'
  },
  {
    title: 'React Native / Expo',
    description:
      'Use Clerk with Expo to authenticate users in your React Native application. ',
    url: '/docs/quickstarts/expo',
    logoIcon: isLightMode
      ? '/images/logos/expo.svg'
      : '/images/logos/expo-white.svg',
    logoSize: 'sm'
  }
];

type BackendSuggestionsCommonProps = {
  title: string;
  description: string;
  url: string;
};

type BackendSuggestionsConditionalPropsWithIconComponent = {
  icon: (props: React.ComponentProps<'svg'>) => JSX.Element;
  logoIcon?: never;
  logoSize?: never;
};

type BackendSuggestionsConditionalPropsWithIconLogo = {
  icon?: never;
  logoIcon: string;
  logoSize: 'sm' | 'md';
};

type BackendSuggestionsProps = BackendSuggestionsCommonProps &
  (
    | BackendSuggestionsConditionalPropsWithIconComponent
    | BackendSuggestionsConditionalPropsWithIconLogo
  );

export const backendSuggestions = (
  isLightMode: boolean
): BackendSuggestionsProps[] => [
  {
    title: 'Node / Express',
    description:
      'Quickly add authentication and user management to your Node.js application.',
    url: '/docs/references/nodejs/overview',
    logoIcon: isLightMode
      ? '/images/logos/node.svg'
      : '/images/logos/node-white.svg',
    logoSize: 'md'
  },
  {
    title: 'Ruby on Rails',
    description:
      'Integrate user management and authentication into your Ruby application.',
    url: '/docs/references/ruby/overview',
    logoIcon: '/images/logos/ruby.svg',
    logoSize: 'sm'
  },
  {
    title: 'SDKs',
    description: `Clerk's SDKs allow you to call the Clerk server API without having to implement the calls yourself.`,
    url: '/docs/references/overview',
    icon: TerminalIcon
  }
];

export const featureSuggestions: Array<{
  title: string;
  description: string;
  url: string;
  icon: (props: React.ComponentProps<'svg'>) => JSX.Element;
}> = [
  {
    title: 'Authentication',
    description:
      'Clerk supports multiple authentication strategies so you can implement the strategy that makes sense for your users.',
    url: '/docs/authentication/overview',
    icon: KeyIcon
  },
  {
    title: 'User management',
    description:
      'Complete user management. Add sign up, sign in, and profile management to your application in minutes.',
    url: '/docs/users/overview',
    icon: UserIcon
  },
  {
    title: 'Database integrations',
    description: `Enable Clerk-managed users to authenticate and interact directly with your database with Clerk's integrations.`,
    url: '/docs/integrations/overview',
    icon: ServerIcon
  },
  {
    title: 'Customization',
    description: `Clerk's components can be customized to match the look and feel of your application.`,
    url: '/docs/components/customization/overview',
    icon: ColorSwatchIcon
  },
  {
    title: 'Organizations',
    description: `Organizations are shared accounts, useful for project and team leaders. Members with elevated privileges can manage member access to the organization's data and resources.`,
    url: '/docs/organizations/overview',
    icon: UserGroupIcon
  }
];

export const conceptsSuggestions: Array<{
  title: string;
  description: string;
  url: string;
}> = [
  {
    title: `What is Clerk authentication?`,
    description: `Clerk offers multiple authentication strategies to identify legitimate users of your appication, and to allow them to make authenticated requests to your backend.`,
    url: '/docs/authentication/overview'
  },
  {
    title: 'What is the "User" object?',
    description: `The User object contains all account information that describes a user of your app in Clerk. Users can authenticate and manage their accounts, update their personal and contact info, or set up security features for their accounts.`,
    url: '/docs/users/overview'
  },
  {
    title: 'How do organizations work?',
    description: `Organizations allow members to collaborate across shared resources. Each member of an organization needs to have a user account in your application. All organization members have access to most of the organization resources, but some members can take advantage of administrative features.`,
    url: '/docs/organizations/overview'
  }
];

export const componentReference: Array<{
  title: string;
  description: string;
  url: string;
  icon: (props: React.ComponentProps<'svg'>) => JSX.Element;
}> = [
  {
    title: 'UI Components',
    description: `Clerk's pre-built UI components give you a beautiful, fully-functional user
    management experience in minutes.`,
    url: '/docs/components/authentication/sign-in',
    icon: SparklesIcon
  },
  {
    title: 'Control Components',
    description: `Clerk's control components render at <Loading /> and <Loaded /> states for assertions on the Clerk object.`,
    url: '/docs/components/control/clerk-loaded',
    icon: ShieldCheckIcon
  },
  {
    title: 'Customization',
    description: `Fully customize our UI components with your own style preferences. With support for Tailwind CSS, Chakra UI, and more.`,
    url: '/docs/components/customization/overview',
    icon: ColorSwatchIcon
  },
  {
    title: 'Internationalization',
    description: `Functionality which enables UI strings to be passed into components so they can be translated in different languages.`,
    url: '/docs/components/customization/localization',
    icon: TranslateIcon
  }
];

export const frontendSDKs: Array<{
  title: string;
  description: string;
  url: string;
  logoIcon: string;
  logoSize: 'sm' | 'md';
}> = [
  {
    title: 'JavaScript',
    description:
      'ClerkJS is our foundational JavaScript library for building user management and authentication.',
    url: '/docs/references/javascript/overview',
    logoIcon: '/images/logos/javascript.svg',
    logoSize: 'sm'
  },
  {
    title: 'React',
    description:
      'Clerk React is the recommended way to integrate Clerk into your React application.',
    url: '/docs/references/react/overview',
    logoIcon: '/images/logos/react.svg',
    logoSize: 'sm'
  },
  {
    title: 'React Native / Expo',
    description:
      'Clerk Expo is the recommended way to integrate Clerk into your React Native application.',
    // TODO: Add /references/expo/overview when the content is ready
    url: '/docs/quickstarts/expo',
    logoIcon: '/images/logos/expo.svg',
    logoSize: 'sm'
  }
];

export const backendSDKs: Array<{
  title: string;
  description: string;
  url: string;
  logoIcon: string;
  logoSize: 'sm' | 'md';
}> = [
  {
    title: 'Go',
    description:
      'Clerk Go SDK is a wrapper around our Backend API written in Golang.',
    url: '/docs/references/go/overview',
    logoIcon: '/images/logos/go.svg',
    logoSize: 'sm'
  },
  {
    title: 'Node.js',
    description:
      'Clerk Node SDK is the recommended way to integrate Clerk into your Node / Express application.',
    url: '/docs/references/nodejs/overview',
    logoIcon: '/images/logos/node.svg',
    logoSize: 'md'
  },
  {
    title: 'Ruby',
    description:
      'Clerk Ruby is the recommended way to integrate Clerk into your Ruby application.',
    url: '/docs/references/ruby/overview',
    logoIcon: '/images/logos/ruby.svg',
    logoSize: 'sm'
  }
];

export const integrationSuggestions = (
  isLightMode: boolean
): Array<{
  title: string;
  description: string;
  url: string;
  logoIcon: string;
  logoSize: 'sm' | 'md';
}> => [
  {
    title: 'Firebase',
    description:
      'Clerk integrates directly with Firebase, a popular Platform-as-a-Service (PaaS) provider, so developers can easily add our beautiful Sign Up, Sign In, and User Profile UIs to their Firebase application.',
    url: '/docs/integrations/databases/firebase',
    logoIcon: '/images/logos/firebase.svg',
    logoSize: 'sm'
  },
  {
    title: 'Supabase',
    description:
      'Clerk embraces the open source alternative to Firebase and provides an integration to Supabase with our JSON Web Tokens (JWT) Templates feature.',
    url: '/docs/integrations/databases/supabase',
    logoIcon: '/images/logos/supabase.svg',
    logoSize: 'sm'
  },
  {
    title: 'Hasura',
    description:
      'Easily connect to your Hasura GraphQL endpoint using Clerk as your authentication provider.',
    url: '/docs/integrations/databases/hasura',
    logoIcon: isLightMode
      ? '/images/logos/hasura.svg'
      : '/images/logos/hasura-white.svg',
    logoSize: 'sm'
  },
  {
    title: 'Fauna',
    description:
      'Build applications using Fauna, the distributed document-relational database, with Clerk as your authentication provider.',
    url: '/docs/integrations/databases/fauna',
    logoIcon: '/images/logos/fauna.svg',
    logoSize: 'sm'
  },
  {
    title: 'Grafbase',
    description:
      'Build applications with serverless GraphQL backends provided by Grafbase and user authencation provided by Clerk.',
    url: '/docs/integrations/databases/grafbase',
    logoIcon: isLightMode
      ? '/images/logos/grafbase.svg'
      : '/images/logos/grafbase-white.svg',
    logoSize: 'sm'
  },
  {
    title: 'Nhost',
    description:
      'The open source alternative to Firebase with GraphQL seemlessly integrates with Clerk to provide authenticated queries.',
    url: '/docs/integrations/databases/nhost',
    logoIcon: isLightMode
      ? '/images/logos/logomark-nhost.svg'
      : '/images/logos/logomark-nhost.svg',
    logoSize: 'sm'
  },
  {
    title: 'Convex',
    description:
      'Clerk integrates with Convex, the serverless state management platform, using our JSON Web Tokens (JWT) Templates feature.',
    url: '/docs/integrations/databases/convex',
    logoIcon: '/images/logos/convex.svg',
    logoSize: 'sm'
  },
  {
    title: 'Google Analytics',
    description:
      'Send user authentication events to your Google Analytics property to enable metrics tracking on user sign up and sign in events.',
    url: '/docs/integrations/analytics/google-analytics',
    logoIcon: '/images/logos/logomark-google-analytics.svg',
    logoSize: 'sm'
  }
];

export const nextjsOverview: Array<{
  title: string;
  description: string;
  url: string;
}> = [
  {
    title: 'Get Started',
    description: 'Easily install Clerk into your Next.js application',
    url: '/docs/quickstarts/nextjs'
  },
  {
    title: 'Embeddable UIs',
    description: 'Embed Clerk UI components into your Next.js application',
    url: '/docs/components/overview'
  },
  {
    title: 'Authenticating your application',
    description: 'Access authentication with Clerk in your Next.js application',
    url: '/docs/references/nextjs/auth-middleware'
  },
  {
    title: 'React Helpers',
    description: 'Leverage Clerk React helpers for your Next.js applicaton',
    url: '/docs/references/react/use-user'
  },
  {
    title: 'Server-side APIs',
    description: 'Access Clerk data using our backend API',
    url: '/docs/references/backend/overview'
  }
];
